/**
 * Common Styling Props
 * Shared styling props that CDS components can accept
 */

import { SpacingToken } from "design-system/foundation/Spacing";
import { SizeToken } from "design-system/foundation/Size";
import { BorderColorToken, SurfaceColorToken } from "design-system/foundation/Color";
import { RadiusToken } from "design-system/foundation/Radius";

/**
 * Common spacing props that components can accept
 */
export interface SpacingProps {
  // Padding
  p?: SpacingToken;
  padding?: SpacingToken;
  pt?: SpacingToken;
  paddingTop?: SpacingToken;
  pr?: SpacingToken;
  paddingRight?: SpacingToken;
  pb?: SpacingToken;
  paddingBottom?: SpacingToken;
  pl?: SpacingToken;
  paddingLeft?: SpacingToken;
  px?: SpacingToken;
  paddingX?: SpacingToken;
  py?: SpacingToken;
  paddingY?: SpacingToken;

  // Margin
  m?: SpacingToken;
  margin?: SpacingToken;
  mt?: SpacingToken;
  marginTop?: SpacingToken;
  mr?: SpacingToken;
  marginRight?: SpacingToken;
  mb?: SpacingToken;
  marginBottom?: SpacingToken;
  ml?: SpacingToken;
  marginLeft?: SpacingToken;
  mx?: SpacingToken;
  marginX?: SpacingToken;
  my?: SpacingToken;
  marginY?: SpacingToken;
}

/**
 * Common sizing props that components can accept
 */
export interface SizingProps {
  width?: SizeToken;
  w?: SizeToken;
  minWidth?: SizeToken;
  minW?: SizeToken;
  maxWidth?: SizeToken;
  maxW?: SizeToken;
  height?: SizeToken;
  h?: SizeToken;
  minHeight?: SizeToken;
  minH?: SizeToken;
  maxHeight?: SizeToken;
  maxH?: SizeToken;
}

/**
 * Common border props that components can accept
 */
export interface BorderProps {
  borderColor?: BorderColorToken;
  borderWidth?: SpacingToken;
  borderRadius?: RadiusToken;
}

/**
 * Common background props
 */
export interface BackgroundProps {
  bg?: SurfaceColorToken;
  backgroundColor?: SurfaceColorToken;
}

/**
 * Combined common styling props
 */
export interface CommonStyleProps extends SpacingProps, SizingProps, BorderProps, BackgroundProps {}

/**
 * Extract CDS tokens from common props and remaining props
 * This separates CDS tokens from other props for proper resolution
 */
export const extractCommonProps = (props: CommonStyleProps & Record<string, any>) => {
  const {
    // Padding props
    p, padding,
    pt, paddingTop,
    pr, paddingRight,
    pb, paddingBottom,
    pl, paddingLeft,
    px, paddingX,
    py, paddingY,

    // Margin props
    m, margin,
    mt, marginTop,
    mr, marginRight,
    mb, marginBottom,
    ml, marginLeft,
    mx, marginX,
    my, marginY,

    // Sizing props
    width, w,
    minWidth, minW,
    maxWidth, maxW,
    height, h,
    minHeight, minH,
    maxHeight, maxH,

    // Border props
    borderColor,
    borderWidth,
    borderRadius,

    // Background props
    bg, backgroundColor,

    ...rest
  } = props;

  const cdsProps = {
    // Padding
    p: p || padding,
    pt: pt || paddingTop,
    pr: pr || paddingRight,
    pb: pb || paddingBottom,
    pl: pl || paddingLeft,
    px: px || paddingX,
    py: py || paddingY,

    // Margin
    m: m || margin,
    mt: mt || marginTop,
    mr: mr || marginRight,
    mb: mb || marginBottom,
    ml: ml || marginLeft,
    mx: mx || marginX,
    my: my || marginY,

    // Sizing
    w: w || width,
    minW: minW || minWidth,
    maxW: maxW || maxWidth,
    h: h || height,
    minH: minH || minHeight,
    maxH: maxH || maxHeight,

    // Border
    borderColor,
    borderWidth,
    borderRadius,

    // Background
    bg: bg || backgroundColor,
  };

  // Filter out undefined values
  const filteredCdsProps = Object.fromEntries(
    Object.entries(cdsProps).filter(([_, value]) => value !== undefined),
  );

  return {
    cdsProps: filteredCdsProps,
    remainingProps: rest,
  };
};
